<template>
  <div class="admin-consults">
    <header class="top">
      <div class="title-row">
        <div>
          <h1>Booth schedule</h1>
          <div class="event">{{ eventName }} · Las Vegas time</div>
        </div>
        <nav class="admin-nav">
          <router-link to="/admin/users" class="nav-tab">Users</router-link>
          <router-link to="/admin/feedback" class="nav-tab">Feedback</router-link>
          <router-link to="/admin/chapter-roasts" class="nav-tab">Chapter Roasts</router-link>
          <router-link to="/admin/consults" class="nav-tab active">Consults</router-link>
        </nav>
      </div>

      <div class="toolbar">
        <div class="days">
          <button
            v-for="d in days"
            :key="d.date"
            class="day"
            :class="{ active: d.date === date }"
            @click="selectDay(d.date)"
          >
            {{ dayLabel(d.date) }}
          </button>
        </div>
        <div class="chips">
          <span class="chip"
            >Booked <b>{{ summary['booked'] ?? 0 }}</b></span
          >
          <span class="chip ok"
            >Checked in <b>{{ (summary['checked_in'] ?? 0) + (summary['walk_in'] ?? 0) }}</b></span
          >
          <span class="chip muted"
            >No-show <b>{{ summary['no_show'] ?? 0 }}</b></span
          >
          <span class="chip open"
            >Open today <b>{{ openToday }}</b></span
          >
        </div>
        <div class="tool-buttons">
          <button class="btn ghost" @click="showEditors = !showEditors">Editors</button>
          <button class="btn primary" @click="openWalkIn(null)">Add walk-in</button>
        </div>
      </div>

      <div v-if="showEditors" class="editors-panel">
        <div v-for="e in editors" :key="e.id" class="editor-row">
          <span class="editor-name" :class="{ inactive: !e.active }">{{ e.name }}</span>
          <label class="toggle">
            <input type="checkbox" :checked="!!e.active" @change="toggleEditor(e)" />
            Taking bookings
          </label>
          <button class="btn small" :disabled="busy" @click="generate(e.id)">
            {{ slotCount(e.id) ? 'Fill missing slots' : 'Create slots' }} for {{ dayLabel(date) }}
          </button>
          <span class="hint">{{ slotCount(e.id) }} slots today</span>
        </div>
        <form class="add-editor" @submit.prevent="addEditor">
          <input v-model="newEditor" placeholder="Editor name" />
          <button class="btn small" :disabled="!newEditor.trim() || busy">Add editor</button>
        </form>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
    </header>

    <div class="layout" :class="{ 'with-drawer': selected }">
      <div class="grid-wrap">
        <div v-if="loading" class="empty">Loading...</div>
        <div v-else-if="!rows.length" class="empty">
          No slots on {{ dayLabel(date) }} yet. Open <b>Editors</b> to create them.
        </div>
        <table v-else class="grid">
          <thead>
            <tr>
              <th class="time-col"></th>
              <th v-for="e in gridEditors" :key="e.id" :class="{ inactive: !e.active }">{{ e.name }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.startsAt"
              :class="{ now: row.startsAt === nowRow, past: row.startsAt < nowRow }"
            >
              <td class="time-col">{{ timeLabel(row.startsAt) }}</td>
              <td v-for="e in gridEditors" :key="e.id">
                <button
                  v-if="row.cells[e.id]"
                  class="cell"
                  :class="[cellKind(row.cells[e.id]!), { selected: selected?.slot.id === row.cells[e.id]!.id }]"
                  @click="select(row.cells[e.id]!)"
                >
                  <template v-if="activeBooking(row.cells[e.id]!) || lastBooking(row.cells[e.id]!)">
                    <span class="who">{{
                      (activeBooking(row.cells[e.id]!) || lastBooking(row.cells[e.id]!))!.name || 'Guest'
                    }}</span>
                    <span class="what">{{
                      statusLabel((activeBooking(row.cells[e.id]!) || lastBooking(row.cells[e.id]!))!.status)
                    }}</span>
                  </template>
                  <template v-else-if="row.cells[e.id]!.status === 'blocked'">
                    <span class="what">{{ blockLabel(row.cells[e.id]!.blockReason) }}</span>
                  </template>
                  <template v-else><span class="what">+ Open</span></template>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <aside v-if="selected" class="drawer">
        <button class="close" aria-label="Close" @click="selected = null">×</button>
        <div class="drawer-time">
          {{ timeLabel(selected.slot.startsAt) }} · {{ editorName(selected.slot.editorId) }}
        </div>

        <template v-if="selected.booking">
          <h2>{{ selected.booking.name || 'Guest' }}</h2>
          <div class="email">{{ selected.booking.email || 'No email' }}</div>
          <div class="pill" :class="selected.booking.status">{{ statusLabel(selected.booking.status) }}</div>
          <div v-if="selected.booking.projectTitle" class="roast-title">Roast: {{ selected.booking.projectTitle }}</div>

          <div class="actions">
            <template v-if="selected.booking.status === 'booked'">
              <button class="btn primary" :disabled="busy" @click="setStatus('checked_in')">Check in</button>
              <button class="btn" :disabled="busy" @click="setStatus('no_show')">No-show</button>
            </template>
            <button
              v-if="selected.booking.status === 'checked_in' || selected.booking.status === 'no_show'"
              class="btn"
              :disabled="busy"
              @click="setStatus('booked')"
            >
              Undo
            </button>
            <button
              v-if="selected.booking.status !== 'cancelled'"
              class="btn danger"
              :disabled="busy"
              @click="setStatus('cancelled')"
            >
              Cancel
            </button>
          </div>

          <div v-if="selected.booking.status === 'booked' || selected.booking.status === 'walk_in'" class="move">
            <select v-model="moveTo">
              <option value="">Move to...</option>
              <option v-for="o in moveOptions" :key="o.id" :value="o.id">{{ o.label }}</option>
            </select>
            <button class="btn small" :disabled="!moveTo || busy" @click="move">Move</button>
          </div>

          <template v-if="selected.booking.projectId">
            <div class="tabs">
              <button :class="{ active: tab === 'roast' }" @click="loadDoc('roast')">Roast</button>
              <button :class="{ active: tab === 'chapter' }" @click="loadDoc('chapter')">Chapter</button>
              <a class="open-link" :href="`/edit/${selected.booking.projectId}`" target="_blank">Open ↗</a>
            </div>
            <div class="doc">
              <div v-if="docLoading" class="hint">Loading...</div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-else class="doc-body" v-html="docHtml"></div>
            </div>
          </template>
        </template>

        <template v-else-if="selected.slot.status === 'blocked'">
          <h2>{{ blockLabel(selected.slot.blockReason) }}</h2>
          <p class="hint">Blocked slots don't show up for authors.</p>
          <button class="btn primary" :disabled="busy" @click="setSlot('open')">Open this slot</button>
        </template>

        <template v-else>
          <h2>Open slot</h2>
          <div class="actions">
            <button class="btn primary" @click="openWalkIn(selected.slot.id)">Seat a walk-in here</button>
            <button class="btn" :disabled="busy" @click="setSlot('blocked')">Block</button>
          </div>
        </template>
      </aside>
    </div>

    <div v-if="walkIn" class="modal-backdrop" @click.self="walkIn = null">
      <form class="modal" @submit.prevent="submitWalkIn">
        <h2>Add walk-in</h2>
        <p class="hint">
          {{ walkIn.slotId ? 'Seats them in the slot you picked.' : 'Seats them in the next open slot.' }}
          An email that matches a Twig account links their roast and follow-up emails.
        </p>
        <input v-model="walkIn.name" placeholder="Name" required />
        <input v-model="walkIn.email" type="email" placeholder="Email (optional)" />
        <div class="actions">
          <button class="btn primary" :disabled="!walkIn.name.trim() || busy">Seat them</button>
          <button type="button" class="btn" @click="walkIn = null">Cancel</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { CONSULT_EVENT, dayLabel, timeLabel, type ConsultDay } from '../lib/consults';
import { parseMarkdown } from '../lib/renderOutput';
import { fetchWithAuth } from '../services/api';

interface Editor {
  id: string;
  name: string;
  active: number;
  sortOrder: number;
}
interface Booking {
  id: string;
  name: string | null;
  email: string | null;
  status: string;
  projectId: string | null;
  projectTitle: string | null;
  updatedAt: string | null;
}
interface Slot {
  id: string;
  editorId: string;
  startsAt: string;
  status: 'open' | 'blocked';
  blockReason: string | null;
  bookings: Booking[];
}

const ACTIVE = ['booked', 'checked_in', 'walk_in'];
const base = `/api/admin/consults/${CONSULT_EVENT}`;

const eventName = ref('');
const days = ref<ConsultDay[]>([]);
const date = ref('');
const editors = ref<Editor[]>([]);
const slots = ref<Slot[]>([]);
const summary = ref<Record<string, number>>({});
const loading = ref(true);
const busy = ref(false);
const error = ref('');
const showEditors = ref(false);
const newEditor = ref('');
const selected = ref<{ slot: Slot; booking: Booking | null } | null>(null);
const moveTo = ref('');
const tab = ref<'roast' | 'chapter'>('roast');
const docHtml = ref('');
const docLoading = ref(false);
const walkIn = ref<{ name: string; email: string; slotId: string | null } | null>(null);
const now = ref(new Date());

const activeBooking = (s: Slot) => s.bookings.find(b => ACTIVE.includes(b.status)) ?? null;
const lastBooking = (s: Slot) => s.bookings.find(b => b.status === 'no_show') ?? null;
const editorName = (id: string) => editors.value.find(e => e.id === id)?.name ?? '';
const slotCount = (editorId: string) => slots.value.filter(s => s.editorId === editorId).length;

const statusLabel = (status: string) =>
  ({ booked: 'Booked', checked_in: 'Checked in', walk_in: 'Walk-in', no_show: 'No-show', cancelled: 'Cancelled' })[
    status
  ] ?? status;
const blockLabel = (reason: string | null) =>
  ({ buffer: 'Buffer', lunch: 'Lunch', manual: 'Blocked' })[reason ?? 'manual'] ?? 'Blocked';

function cellKind(s: Slot) {
  const b = activeBooking(s);
  if (b) return b.status === 'booked' ? 'booked' : 'checked';
  if (lastBooking(s)) return 'no-show';
  if (s.status === 'blocked')
    return s.blockReason === 'lunch' ? 'lunch' : s.blockReason === 'buffer' ? 'buffer' : 'blocked';
  return 'open';
}

// Editors with slots today, plus active ones without any yet
const gridEditors = computed(() => editors.value.filter(e => e.active || slotCount(e.id)));

const rows = computed(() => {
  const byTime = new Map<string, Record<string, Slot>>();
  for (const s of slots.value) {
    if (!byTime.has(s.startsAt)) byTime.set(s.startsAt, {});
    byTime.get(s.startsAt)![s.editorId] = s;
  }
  return [...byTime.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([startsAt, cells]) => ({ startsAt, cells }));
});

// The row happening now
const nowRow = computed(() => {
  const t = now.value.toISOString();
  return [...rows.value].reverse().find(r => r.startsAt <= t)?.startsAt ?? '';
});

const openToday = computed(
  () =>
    slots.value.filter(
      s =>
        s.status === 'open' &&
        !activeBooking(s) &&
        s.startsAt >= now.value.toISOString() &&
        editors.value.find(e => e.id === s.editorId)?.active
    ).length
);

const moveOptions = computed(() =>
  slots.value
    .filter(s => s.status === 'open' && !activeBooking(s) && s.id !== selected.value?.slot.id)
    .map(s => ({ id: s.id, label: `${timeLabel(s.startsAt)} · ${editorName(s.editorId)}` }))
);

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetchWithAuth(`${base}${path}`, {
    ...init,
    ...(init.body ? { headers: { 'Content-Type': 'application/json' } } : {}),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

async function load() {
  try {
    const data = await api<{
      event: { name: string; days: ConsultDay[] };
      date: string;
      editors: Editor[];
      slots: Slot[];
      summary: Record<string, number>;
    }>(`/schedule${date.value ? `?date=${date.value}` : ''}`);
    eventName.value = data.event.name;
    days.value = data.event.days;
    date.value = data.date;
    editors.value = data.editors;
    slots.value = data.slots;
    summary.value = data.summary;
    // Keep the drawer pointed at fresh data
    if (selected.value) {
      const slot = slots.value.find(s => s.id === selected.value!.slot.id);
      selected.value = slot ? { slot, booking: activeBooking(slot) ?? lastBooking(slot) } : null;
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load';
  } finally {
    loading.value = false;
  }
}

async function run(action: () => Promise<unknown>) {
  busy.value = true;
  error.value = '';
  try {
    await action();
    await load();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Something failed';
  } finally {
    busy.value = false;
  }
}

function selectDay(d: string) {
  date.value = d;
  selected.value = null;
  loading.value = true;
  void load();
}

function select(slot: Slot) {
  const booking = activeBooking(slot) ?? lastBooking(slot);
  selected.value = { slot, booking };
  moveTo.value = '';
  docHtml.value = '';
  if (booking?.projectId) void loadDoc('roast');
}

async function loadDoc(which: 'roast' | 'chapter') {
  const projectId = selected.value?.booking?.projectId;
  if (!projectId) return;
  tab.value = which;
  docLoading.value = true;
  try {
    const url =
      which === 'roast'
        ? `/api/projects/${projectId}/files/chapter-roast/chapter_roast_letter`
        : `/api/admin/consults/projects/${projectId}/chapter`;
    const res = await fetchWithAuth(url);
    const data = (await res.json()) as { content?: string; text?: string };
    docHtml.value = parseMarkdown(data.content ?? data.text ?? 'Not available.');
  } catch {
    docHtml.value = '<p>Not available.</p>';
  } finally {
    docLoading.value = false;
  }
}

const setStatus = (status: string) =>
  run(() =>
    api(`/bookings/${selected.value!.booking!.id}/status`, { method: 'POST', body: JSON.stringify({ status }) })
  );
const move = () =>
  run(() =>
    api(`/bookings/${selected.value!.booking!.id}/move`, {
      method: 'POST',
      body: JSON.stringify({ slotId: moveTo.value }),
    })
  );
const setSlot = (status: 'open' | 'blocked') =>
  run(() => api(`/slots/${selected.value!.slot.id}`, { method: 'PATCH', body: JSON.stringify({ status }) }));
const toggleEditor = (e: Editor) =>
  run(() => api(`/editors/${e.id}`, { method: 'PATCH', body: JSON.stringify({ active: !e.active }) }));
const generate = (editorId: string) =>
  run(() => api('/slots/generate', { method: 'POST', body: JSON.stringify({ editorId, date: date.value }) }));

async function addEditor() {
  const name = newEditor.value.trim();
  await run(() => api('/editors', { method: 'POST', body: JSON.stringify({ name }) }));
  newEditor.value = '';
}

function openWalkIn(slotId: string | null) {
  walkIn.value = { name: '', email: '', slotId };
}

async function submitWalkIn() {
  const w = walkIn.value!;
  await run(() =>
    api('/walk-ins', {
      method: 'POST',
      body: JSON.stringify({ name: w.name, email: w.email || undefined, slotId: w.slotId || undefined }),
    })
  );
  if (!error.value) walkIn.value = null;
}

let timer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  document.title = 'Booth schedule | Twig';
  void load();
  // Bookings keep arriving during the show; refresh quietly
  timer = setInterval(() => {
    now.value = new Date();
    if (!busy.value) void load();
  }, 30000);
});
onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.admin-consults {
  flex: 1;
  background: #fff;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
.top {
  padding: 20px 24px 12px;
  border-bottom: 1px solid var(--color-border-primary);
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 5;
}
.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}
h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
}
.event {
  color: var(--color-text-secondary);
  font-size: 13px;
}
.admin-nav {
  display: flex;
  gap: 4px;
}
.nav-tab {
  padding: 6px 12px;
  border-radius: 6px;
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: 14px;
}
.nav-tab.active {
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-weight: 600;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.days {
  display: flex;
  gap: 4px;
  padding: 3px;
  background: var(--color-bg-tertiary);
  border-radius: 10px;
}
.day {
  border: 0;
  background: transparent;
  padding: 10px 16px;
  border-radius: 8px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.day.active {
  background: #fff;
  box-shadow: var(--shadow-sm);
}
.chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.chip {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f0f7fc;
  color: #1a5f8a;
}
.chip.ok {
  background: #f2faf5;
  color: var(--color-success);
}
.chip.muted {
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
}
.chip.open {
  background: #fff;
  border: 1px dashed var(--color-border-secondary);
  color: var(--color-text-secondary);
}
.tool-buttons {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.btn {
  border: 1px solid var(--color-border-secondary);
  background: #fff;
  border-radius: var(--border-radius);
  padding: 10px 16px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
}
.btn.small {
  padding: 6px 12px;
  min-height: 36px;
  font-weight: 500;
}
.btn.primary {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}
.btn.danger {
  color: var(--color-danger);
}
.btn.ghost {
  background: var(--color-bg-secondary);
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.editors-panel {
  margin-top: 12px;
  padding: 12px 16px;
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius);
  display: grid;
  gap: 8px;
}
.editor-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.editor-name {
  font-weight: 600;
  min-width: 120px;
}
.editor-name.inactive {
  color: var(--color-text-muted);
}
.toggle {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 13px;
}
.add-editor {
  display: flex;
  gap: 8px;
}
.add-editor input,
.modal input,
.move select {
  border: 1px solid var(--color-border-secondary);
  border-radius: var(--border-radius);
  padding: 9px 12px;
  font: inherit;
}
.hint {
  color: var(--color-text-muted);
  font-size: 13px;
}
.error {
  color: var(--color-danger);
  margin: 8px 0 0;
}
.layout {
  display: flex;
  flex: 1;
  min-height: 0;
}
.grid-wrap {
  flex: 1;
  overflow: auto;
  padding: 12px 24px 48px;
}
.empty {
  padding: 48px 0;
  color: var(--color-text-secondary);
}
.grid {
  border-collapse: separate;
  border-spacing: 6px 4px;
  width: 100%;
}
.grid th {
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  padding: 4px 8px;
  position: sticky;
  top: 0;
  background: #fff;
}
.grid th.inactive {
  color: var(--color-text-muted);
}
.time-col {
  width: 72px;
  font-size: 13px;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
tr.now .time-col {
  color: var(--color-danger);
  font-weight: 700;
}
tr.now td {
  box-shadow: inset 0 2px 0 rgba(207, 34, 46, 0.35);
}
tr.past .cell {
  opacity: 0.6;
}
.cell {
  width: 100%;
  min-height: 46px;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  background: #fff;
  padding: 6px 10px;
  text-align: left;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.cell .who {
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cell .what {
  font-size: 12px;
  color: var(--color-text-secondary);
}
.cell.open {
  border-style: dashed;
  border-color: var(--color-border-secondary);
}
.cell.open .what {
  color: var(--color-text-muted);
}
.cell.booked {
  border-left: 4px solid #1a5f8a;
  background: #f0f7fc;
}
.cell.checked {
  border-left: 4px solid var(--color-success);
  background: #f2faf5;
}
.cell.no-show .who {
  text-decoration: line-through;
  color: var(--color-text-muted);
}
.cell.buffer {
  background: repeating-linear-gradient(135deg, #f6f7f8, #f6f7f8 6px, #eef0f2 6px, #eef0f2 12px);
  border-color: transparent;
}
.cell.lunch,
.cell.blocked {
  background: var(--color-bg-tertiary);
  border-color: transparent;
}
.cell.selected {
  outline: 2px solid var(--color-accent);
  outline-offset: 1px;
}
.drawer {
  width: 400px;
  flex: none;
  border-left: 1px solid var(--color-border-primary);
  padding: 20px 24px;
  overflow-y: auto;
  position: relative;
  background: #fff;
}
.close {
  position: absolute;
  top: 12px;
  right: 14px;
  border: 0;
  background: none;
  font-size: 26px;
  cursor: pointer;
  color: var(--color-text-muted);
}
.drawer-time {
  font-size: 13px;
  color: var(--color-text-secondary);
}
.drawer h2 {
  font-size: 22px;
  margin: 4px 0 2px;
  font-weight: 600;
}
.email {
  color: var(--color-text-secondary);
  font-size: 14px;
}
.pill {
  display: inline-block;
  margin-top: 8px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #f0f7fc;
  color: #1a5f8a;
}
.pill.checked_in,
.pill.walk_in {
  background: #f2faf5;
  color: var(--color-success);
}
.pill.no_show,
.pill.cancelled {
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
}
.roast-title {
  margin-top: 8px;
  font-size: 14px;
}
.actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
}
.move {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.move select {
  flex: 1;
}
.tabs {
  display: flex;
  gap: 4px;
  margin-top: 20px;
  border-bottom: 1px solid var(--color-border-primary);
  align-items: center;
}
.tabs button {
  border: 0;
  background: none;
  padding: 8px 12px;
  font: inherit;
  font-weight: 500;
  cursor: pointer;
  color: var(--color-text-secondary);
  border-bottom: 2px solid transparent;
}
.tabs button.active {
  color: var(--color-text-primary);
  border-bottom-color: var(--color-accent);
}
.open-link {
  margin-left: auto;
  font-size: 13px;
  color: var(--color-accent-hover);
  text-decoration: none;
}
.doc {
  padding-top: 12px;
}
.doc-body {
  font-family: 'PT Serif', serif;
  font-size: 15px;
  line-height: 1.65;
}
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: grid;
  place-items: center;
  z-index: 50;
  padding: 16px;
}
.modal {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
  display: grid;
  gap: 10px;
}
.modal h2 {
  margin: 0;
  font-size: 20px;
}
@media (max-width: 900px) {
  .drawer {
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    width: min(400px, 100%);
    z-index: 30;
    box-shadow: var(--shadow-xl);
  }
}
</style>
